import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { InteractiveGlobe } from '../components/ui/interactive-globe';
import { Features } from '../components/ui/features';
import { CategoryList } from '../components/ui/category-list';
import ProcessFlow from '../components/ui/process-flow';
import {
  ArrowRight,
  Shield,
  Target,
  Users,
  BarChart3,
  CheckCircle2,
  Globe,
  Zap,
  FileCheck,
  TrendingUp,
  Handshake,
  BadgeCheck,
  Search,
  CalendarCheck,
  Upload,
  Workflow
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Function to open seller signup dialog
  const openSellerSignup = () => {
    window.dispatchEvent(new CustomEvent('openSignupDialog', { detail: { type: 'seller' } }));
  };

  // Why Choose Us features with images
  const whyChooseUsFeatures = [
    {
      id: 1,
      icon: Target,
      title: 'Precision Targeting',
      description: 'Advanced filters for region, industry, job title, company size, and intent level. Find exactly the leads that match your ideal customer profile.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    },
    {
      id: 2,
      icon: Shield,
      title: 'GDPR & Compliance Ready',
      description: 'All leads come with documented consent and source verification. Stay compliant with international data protection regulations.',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
    },
    {
      id: 3,
      icon: Zap,
      title: 'Fast Delivery',
      description: 'Most orders delivered within 5-10 business days with real-time tracking. Monitor progress directly in your dashboard.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    },
    {
      id: 4,
      icon: BadgeCheck,
      title: 'Quality Guaranteed',
      description: '100% replacement policy for invalid leads. We dedupe against your database to ensure you only pay for new, verified contacts.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
    },
  ];

  const features = [
    {
      icon: Target,
      title: 'Precision Targeting',
      description: 'Filter by region, industry, job title, company size, and intent level.',
    },
    {
      icon: Shield,
      title: 'GDPR Compliant',
      description: 'All leads come with documented consent and source verification.',
    },
    {
      icon: Zap,
      title: 'Fast Delivery',
      description: 'Most orders delivered within 5-10 business days with real-time tracking.',
    },
    {
      icon: FileCheck,
      title: 'Quality Guaranteed',
      description: '100% replacement policy for invalid leads. Dedupe against your database.',
    },
  ];

  return (
    <Layout>
      {/* Hero Section with Globe */}
      <section className="relative overflow-hidden bg-slate-950 min-h-[600px]" data-testid="hero-section">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(249, 115, 22, 0.1) 1px, transparent 1px), 
                             linear-gradient(90deg, rgba(249, 115, 22, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        {/* Ambient glow */}
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full bg-orange-500/5 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 lg:px-8 relative">
          <div className="flex flex-col lg:flex-row items-center min-h-[600px] gap-8 lg:gap-0">
            {/* Left content */}
            <div className="flex-1 flex flex-col justify-center py-12 lg:py-0 lg:pr-8 z-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-sm text-orange-400 mb-6 w-fit">
                <span className="size-2 rounded-full bg-emerald-400 animate-pulse" />
                B2B Lead Generation Marketplace
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1] mb-6">
                Global{' '}
                <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                  Lead Network
                </span>
              </h1>

              <p className="text-base md:text-lg text-slate-400 max-w-lg leading-relaxed mb-8">
                Connect with verified B2B buyers and sellers worldwide. 
                High-quality leads delivered to your AALIGN platform with full compliance.
                Drag the globe to explore our network.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Button 
                  size="lg" 
                  onClick={function() { navigate('/marketplace'); }}
                  className="text-base px-8 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="browse-marketplace-btn"
                >
                  Browse Campaigns
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                {!isAuthenticated && (
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={openSellerSignup}
                    className="text-base px-8 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    data-testid="become-seller-btn"
                  >
                    Become a Seller
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-6 lg:gap-8">
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-white">50K+</p>
                  <p className="text-xs lg:text-sm text-slate-500">Leads Delivered</p>
                </div>
                <div className="w-px h-10 bg-slate-700" />
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-white">300+</p>
                  <p className="text-xs lg:text-sm text-slate-500">Active Buyers</p>
                </div>
                <div className="w-px h-10 bg-slate-700" />
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-white">98%</p>
                  <p className="text-xs lg:text-sm text-slate-500">Satisfaction</p>
                </div>
              </div>
            </div>

            {/* Right - Globe */}
            <div className="flex-1 flex items-center justify-center min-h-[400px] lg:min-h-[600px]">
              <InteractiveGlobe 
                size={480} 
                dotColor="rgba(249, 115, 22, ALPHA)"
                arcColor="rgba(249, 115, 22, 0.4)"
                markerColor="rgba(251, 146, 60, 1)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Animated Features */}
      <section data-testid="features-section">
        <Features 
          features={whyChooseUsFeatures}
          title="Why Choose Us"
          subtitle="Built for B2B Lead Generation Success"
          primaryColor="orange-500"
          progressGradientLight="bg-gradient-to-r from-orange-400 to-orange-500"
          progressGradientDark="bg-gradient-to-r from-orange-300 to-orange-400"
        />
      </section>

      {/* How It Works Section */}
      <section className="bg-slate-50 py-16" data-testid="how-it-works-section">
        <CategoryList
          title="How It"
          subtitle="Works"
          headerIcon={<Workflow className="w-8 h-8" />}
          categories={[
            {
              id: 1,
              title: 'Browse Campaigns',
              subtitle: 'Explore buyer campaigns and find opportunities that match your lead generation capabilities and target markets.',
              icon: <Search className="w-8 h-8" />,
              onClick: () => navigate('/marketplace'),
              featured: true,
            },
            {
              id: 2,
              title: 'Book & Commit',
              subtitle: 'Select lead types, specify quantities, set your delivery timeline, and commit to campaign requirements.',
              icon: <CalendarCheck className="w-8 h-8" />,
            },
            {
              id: 3,
              title: 'Deliver & Earn',
              subtitle: 'Upload verified leads through our platform, get them validated, and receive payment upon approval.',
              icon: <Upload className="w-8 h-8" />,
            },
          ]}
        />
      </section>

      {/* Complete Process Flow Section */}
      <ProcessFlow />

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-500 to-amber-500 py-16" data-testid="cta-section">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Grow Your Pipeline?
          </h2>
          <p className="text-lg text-orange-100 max-w-2xl mx-auto mb-8">
            Join hundreds of companies already using AALIGN Marketplace
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={function() { navigate('/marketplace'); }}
              className="text-base px-8 bg-white text-orange-600 hover:bg-orange-50"
              data-testid="get-started-cta-btn"
            >
              Browse Campaigns
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
